import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router";
import { acceptPairingCodeRequestSchema } from "@wodiary/shared";
import { api } from "../api/client.js";
import { usePairing } from "../api/queries.js";
import { errorMessage } from "../lib/errors.js";
import { pairingCopy } from "../lib/pairing.js";
import { formatDateTime } from "../lib/format.js";
import { NavBar } from "../components/NavBar.js";
import { Loading, QueryError } from "../components/AsyncState.js";
import { useToast } from "../components/ToastContext.js";
import { CheckIcon, LockIcon } from "../components/icons.js";

/**
 * 配对 —— 6 位码、双向确认（spec §5.2）。
 *
 * 五个状态各是画面的一格：
 * - `unpaired`：两个入口并列（生成自己的码 / 输入对方的码）
 * - `awaiting_partner`：出示码 + 过期时间 + 重新生成
 * - `awaiting_my_confirm`：对方已输入，我按最后一次确认
 * - `awaiting_partner_confirm`：我输入了，等对方确认
 * - `active`：配对成功
 *
 * 「配对前双方读不到对方任何日记」是后端的保证（spec §4.4 T1/T2），这一页
 * 只负责把状态说清楚，不负责（也无法）拦截任何数据。
 */
export function PairingPage() {
  const pairing = usePairing();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["pairing"] });

  const generate = useMutation({
    mutationFn: api.generateCode,
    onSuccess: invalidate,
  });
  const confirm = useMutation({ mutationFn: api.confirmPairing, onSuccess: invalidate });
  const accept = useMutation({
    mutationFn: api.acceptCode,
    onSuccess: invalidate,
  });

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const view = pairing.data;

  return (
    <>
      <NavBar title="配对" back onBack={() => navigate("/settings")} />
      <div className="page">
        {pairing.isLoading && <Loading />}
        {pairing.isError && (
          <QueryError error={pairing.error} onRetry={() => void pairing.refetch()} />
        )}

        {view !== undefined && (
          <>
            <div className="pair-card" style={{ background: "var(--brand-navy)" }}>
              <div className="grow">
                <div className="pair-info" style={{ gap: 4 }}>
                  <span className="l1" style={{ fontSize: 15 }}>
                    {pairingCopy(view.state, view.partnerUsername).title}
                  </span>
                  <span className="l2" style={{ lineHeight: "19px" }}>
                    {pairingCopy(view.state, view.partnerUsername).description}
                  </span>
                </div>
              </div>
            </div>

            {view.state === "unpaired" && (
              <>
                <section className="card stack">
                  <div>
                    <div className="section-title">我生成，TA 输入</div>
                    <p className="small ghost-text" style={{ marginTop: 4 }}>
                      生成一个 6 位码，把它当面或通过其它方式给 TA。24 小时内有效，
                      只能用一次。
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary btn-block"
                    disabled={generate.isPending}
                    onClick={() => {
                      setError(null);
                      generate.mutate(undefined, {
                        onError: (e) => setError(errorMessage(e)),
                      });
                    }}
                  >
                    {generate.isPending ? "生成中…" : "生成配对码"}
                  </button>
                </section>

                <section className="card stack">
                  <div>
                    <div className="section-title">TA 生成了码，我来输入</div>
                    <p className="small ghost-text" style={{ marginTop: 4 }}>
                      输入 TA 给你的 6 位码。输完之后，需要 TA 再确认一次配对才会
                      生效。
                    </p>
                  </div>
                  <form
                    className="stack-sm"
                    onSubmit={(e: FormEvent) => {
                      e.preventDefault();
                      setError(null);
                      const parsed = acceptPairingCodeRequestSchema.safeParse({
                        code: code.trim(),
                      });
                      if (!parsed.success) {
                        setError("配对码是 6 位数字。");
                        return;
                      }
                      accept.mutate(parsed.data.code, {
                        onSuccess: () => setCode(""),
                        onError: (err) => setError(errorMessage(err)),
                      });
                    }}
                  >
                    <span className="field">
                      <input
                        value={code}
                        onChange={(e) =>
                          setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                        }
                        placeholder="6 位数字"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                      />
                    </span>
                    <button
                      type="submit"
                      className="btn btn-ghost btn-block"
                      disabled={accept.isPending || code.trim().length !== 6}
                    >
                      {accept.isPending ? "提交中…" : "输入并继续"}
                    </button>
                  </form>
                </section>
              </>
            )}

            {view.state === "awaiting_partner" && view.code !== undefined && (
              <section className="card stack">
                <div className="code-display" aria-label="配对码">
                  {view.code}
                </div>
                {view.codeExpiresAt !== undefined && (
                  <p className="small ghost-text" style={{ textAlign: "center" }}>
                    有效期至 {formatDateTime(view.codeExpiresAt)}
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn-ghost btn-block"
                  onClick={() => {
                    const clip = navigator.clipboard;
                    if (clip === undefined) {
                      toast.show("这个浏览器不支持自动复制，请手动记下");
                      return;
                    }
                    void clip.writeText(view.code ?? "").then(
                      () => toast.show("已复制配对码"),
                      () => toast.show("复制失败，请手动记下"),
                    );
                  }}
                >
                  复制配对码
                </button>
                <button
                  type="button"
                  className="btn btn-quiet btn-block"
                  disabled={generate.isPending}
                  onClick={() => generate.mutate(undefined)}
                >
                  重新生成
                </button>
              </section>
            )}

            {view.state === "awaiting_partner" && view.code === undefined && (
              <section className="card stack">
                <p className="small muted">
                  配对码已过期。重新生成一个，再给 TA。
                </p>
                <button
                  type="button"
                  className="btn btn-primary btn-block"
                  disabled={generate.isPending}
                  onClick={() => generate.mutate(undefined)}
                >
                  重新生成
                </button>
              </section>
            )}

            {view.state === "awaiting_my_confirm" && (
              <section className="card stack">
                <div className="banner banner-info">
                  <CheckIcon size={16} />
                  <span>
                    {view.partnerUsername ?? "TA"} 已经在自己的设备上输入了你的
                    配对码。确认之后配对生效。
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-block"
                  disabled={confirm.isPending}
                  onClick={() => {
                    setError(null);
                    confirm.mutate(undefined, {
                      onError: (e) => setError(errorMessage(e)),
                    });
                  }}
                >
                  {confirm.isPending ? "确认中…" : `确认与 ${view.partnerUsername ?? "TA"} 配对`}
                </button>
              </section>
            )}

            {view.state === "awaiting_partner_confirm" && (
              <section className="card stack">
                <p className="small muted">
                  已经把你的输入交给 {view.partnerUsername ?? "TA"} 了。等 TA 确认
                  之后，配对才会生效 —— 在那之前，双方都还读不到对方的任何日记。
                </p>
              </section>
            )}

            {view.state === "active" && (
              <section className="card stack">
                <div className="banner banner-info">
                  <LockIcon size={16} />
                  <span>
                    已与 {view.partnerUsername ?? "TA"} 配对。此后双方能读到彼此
                    <b>共享</b>的日记；各自写给自己看的日记仍然只属于自己。
                  </span>
                </div>
                <Link to="/" className="btn btn-primary btn-block">
                  去看我们的日记
                </Link>
              </section>
            )}

            {error !== null && <div className="banner banner-error">{error}</div>}
          </>
        )}
      </div>
    </>
  );
}
